import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../apiClient';

/*
  Ad channels — connect an ad account so conversions upload and spend comes back.

  Tenant-generic by construction: the rows come from GET /marketing/channels/, which
  returns every network the backend knows about with its LIVE status. Adding a network
  is a backend change plus one entry in FIELDS here — never a new page.

  Two rules this panel exists to keep visible:

  1. The status shown is the LIVE one the server just verified, not "we saved a value".
     A revoked token has to read Error here, because the symptom otherwise is invisible:
     conversions stop uploading and the ad account quietly gets worse at finding
     customers, with nothing on screen to say why.

  2. The credential is write-only. It goes to the server, straight into Secrets Manager,
     and is never read back — so there is nothing to render and the field always starts
     empty, including after a successful connect.
*/

const STATUS_TONE = {
  connected: { label: 'Connected', cls: 'ok' },
  needs_attention: { label: 'Needs attention', cls: 'warn' },
  connecting: { label: 'Connecting…', cls: 'warn' },
  error: { label: 'Error', cls: 'bad' },
  not_connected: { label: 'Not connected', cls: 'idle' },
};

/* Per-network paste-in fields. `secret: true` means write-only: password input, never
   echoed back, cleared after a connect. */
const FIELDS = {
  meta: {
    blurb:
      'One dataset per business. The website pixel, the booked appointments, signed ' +
      'quotes and first services the CRM sends later, and matched phone calls all land ' +
      'in this one dataset.',
    fields: [
      {
        name: 'dataset_id',
        label: 'Dataset ID',
        placeholder: '1234567890',
        help: 'Events Manager → Data sources → your dataset. The numeric ID under the name.',
      },
      {
        name: 'access_token',
        label: 'System user access token',
        secret: true,
        placeholder: 'EAAG…',
        help: 'Business Settings → System users → Generate token, with ads_management and business_management. Stored encrypted; never shown again.',
      },
      {
        name: 'ad_account_id',
        label: 'Ad account ID',
        optional: true,
        placeholder: 'act_1234567890',
        help: 'Optional. Only needed to pull spend for cost-per-lead — conversions upload without it.',
      },
      {
        name: 'test_event_code',
        label: 'Test event code',
        optional: true,
        placeholder: 'TEST12345',
        help: 'Optional. Events Manager → Test Events. Lets you watch a server event arrive before going live.',
      },
    ],
  },
  openai: {
    blurb: 'ChatGPT Ads conversions, uploaded from the CRM as leads become customers.',
    fields: [
      { name: 'pixel_id', label: 'Pixel ID', placeholder: '6aSW…' },
      { name: 'api_key', label: 'Conversions API key', secret: true, placeholder: 'sk-…' },
    ],
  },
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.not_connected;
  return <span className={`settings-pill settings-pill--${tone.cls}`}>{tone.label}</span>;
}

function ChannelCard({ channel, onConnect, onVerify, busy }) {
  const spec = FIELDS[channel.network];
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const set = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const ok = await onConnect(channel.network, values);
    // Clear the whole form either way: a failed attempt should not leave a token
    // sitting in a DOM input for the next person at this screen.
    setValues({});
    if (ok) setOpen(false);
  };

  const cfg = channel.public_config || {};
  const shownId = cfg.dataset_id || cfg.pixel_id || (cfg.conversion_sources || [])[0]?.pixel_id || '';

  return (
    <div className='settings-card'>
      <div className='row-between'>
        <div>
          <strong>{channel.network_label}</strong>
          {shownId ? <p className='settings-hint'>ID {shownId}</p> : null}
        </div>
        <StatusPill status={channel.status} />
      </div>

      {channel.status_detail ? <p className='settings-hint'>{channel.status_detail}</p> : null}
      {channel.last_verified_at ? (
        <p className='settings-hint'>
          Last checked {new Date(channel.last_verified_at).toLocaleString()}
        </p>
      ) : null}

      {!spec ? (
        <p className='settings-hint'>
          {channel.network_label} connects through OAuth, which is not available yet.
        </p>
      ) : (
        <>
          <div className='row-between'>
            <button type='button' className='btn-secondary' onClick={() => setOpen((v) => !v)}>
              {open ? 'Cancel' : channel.status === 'not_connected' ? 'Connect' : 'Replace credential'}
            </button>
            {channel.status !== 'not_connected' ? (
              <button
                type='button'
                className='btn-secondary'
                disabled={busy}
                onClick={() => onVerify(channel.network)}
              >
                Check connection
              </button>
            ) : null}
          </div>

          {open ? (
            <form onSubmit={submit} className='settings-card two-col'>
              {spec.blurb ? <p className='settings-hint'>{spec.blurb}</p> : null}
              {spec.fields.map((f) => (
                <label key={f.name}>
                  {f.label}
                  {f.optional ? ' (optional)' : ''}
                  <input
                    type={f.secret ? 'password' : 'text'}
                    autoComplete={f.secret ? 'new-password' : 'off'}
                    value={values[f.name] || ''}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                  {f.help ? <span className='settings-hint'>{f.help}</span> : null}
                </label>
              ))}
              <button type='submit' className='btn-primary' disabled={busy}>
                {busy ? 'Checking…' : 'Connect and verify'}
              </button>
            </form>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function AdChannelsPanel() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/marketing/channels/');
      setChannels(data?.channels || []);
      setDenied(false);
    } catch (err) {
      // 403 is not a failure to report as breakage — it is the role gate doing its job.
      if (err?.response?.status === 403) setDenied(true);
      else setMessage('Could not load ad channels.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const connect = async (network, values) => {
    setBusy(true);
    setMessage('');
    try {
      const { data } = await api.post('/marketing/channels/connect/', { network, ...values });
      await load();
      // Connect runs a live check server-side, so the status in the response is the
      // verified one — say exactly what it found rather than "saved".
      setMessage(
        data?.status === 'connected'
          ? `${data.network_label} connected and verified.`
          : `${data?.network_label || network}: ${data?.status_detail || data?.status || 'not verified'}`
      );
      return data?.status === 'connected';
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Could not connect that account.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const verify = async (network) => {
    setBusy(true);
    setMessage('');
    try {
      const { data } = await api.post('/marketing/channels/verify/', { network });
      await load();
      setMessage(data?.status_detail || 'Checked.');
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Could not check that account.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p>Loading ad channels…</p>;
  if (denied) {
    return (
      <p className='settings-hint'>
        Only an owner or admin can see and connect ad accounts — an ad credential is
        spend authority.
      </p>
    );
  }

  return (
    <div>
      <div className='row-between'>
        <h3>Ad accounts</h3>
        <span className='settings-pill'>
          {channels.filter((c) => c.status === 'connected').length} connected
        </span>
      </div>
      <p className='settings-sub'>
        Connecting an ad account does two things: the conversions that happen in here —
        a booked appointment, a signed quote, a first service — upload back to the ad
        network so it can find more people like that, and the spend comes back so the
        marketing report can show cost per customer. Nothing here can change a campaign
        or spend money.
      </p>
      {message ? <p className='settings-msg'>{message}</p> : null}
      {channels.map((c) => (
        <ChannelCard
          key={c.network}
          channel={c}
          onConnect={connect}
          onVerify={verify}
          busy={busy}
        />
      ))}
    </div>
  );
}
